/** On-demand UI only. Candidate requests and HTML rendering belong to FAFRecommendations. */
(() => {
  const register = () => {
    if (!window.Alpine || window.__fafContextualRecommendationsRegistered) return;
    window.__fafContextualRecommendationsRegistered = true;

    Alpine.data('fafRecommendationDialog', () => ({
      status: 'idle', sourceId: '', sourceTitle: '', surface: 'pdp',
      curatedIds: [], count: 0, retryable: false, sequence: 0,
      controller: null, observer: null, trigger: null, locked: false,
      destroyed: false,
      closing: false,
      closeTimer: null,
      closeTransitionHandler: null,
      cartAddedHandler: null,

      init() {
        this.$root.dataset.fafReady = 'true';
        this.$root.dataset.state = 'closed';
        this.cartAddedHandler = (event) => this.trackCartAdded(event);
        window.addEventListener('cart-added', this.cartAddedHandler);
      },

      clearCloseWait() {
        if (this.closeTimer) window.clearTimeout(this.closeTimer);
        if (this.closeTransitionHandler) this.$root.removeEventListener('transitionend', this.closeTransitionHandler);
        this.closeTimer = null;
        this.closeTransitionHandler = null;
      },

      track(event, properties = {}) {
        try {
          Promise.resolve(window.FAFAnalytics?.custom?.(event, {
            source_product_id: this.sourceId,
            recommendation_intent: 'complete_look',
            surface: this.surface,
            recommendation_id: this.$root.dataset.widgetId || '',
            ...properties
          })).catch((error) => console.warn('Recommendation analytics failed.', error));
        } catch (error) {
          console.warn('Recommendation analytics failed.', error);
        }
      },

      trackCartAdded(event) {
        if (!this.$root.open || this.status !== 'success') return;
        const item = event?.detail?.item;
        if (!item || typeof item !== 'object') return;
        const recommendedProductId = String(item.product_id || '').trim();
        const variantId = String(item.variant_id || item.id || '').trim();
        if (!/^[1-9]\d*$/.test(recommendedProductId) || !/^[1-9]\d*$/.test(variantId)) return;
        const cards = Array.from(this.$refs.results?.querySelectorAll('[data-faf-recommendation-id]') || []);
        const position = cards.findIndex((card) => String(card.dataset.fafRecommendationId || '') === recommendedProductId);
        if (position < 0) return;
        this.track('faf_recommendation_add_to_cart', {
          recommended_product_id: recommendedProductId,
          variant_id: variantId,
          quantity: Math.max(1, Number(item.quantity || 1)),
          recommendation_position: position + 1,
          recommendation_list: 'contextual_recommendations_complete_look'
        });
      },

      openFrom(trigger) {
        if (this.destroyed || !trigger?.dataset) return;
        const id = String(trigger.dataset.productId || '').trim();
        if (!/^[1-9]\d*$/.test(id)) return;
        if (typeof this.$root.showModal !== 'function') return;
        if (this.$root.open && this.sourceId === id && this.status === 'loading') return;
        this.clearCloseWait();
        this.closing = false;
        this.trigger = trigger;
        this.sourceId = id;
        this.sourceTitle = String(trigger.dataset.productTitle || '');
        this.surface = trigger.dataset.surface === 'order_history' ? 'order_history' : 'pdp';
        try {
          const parsed = JSON.parse(trigger.dataset.curatedIds || '[]');
          this.curatedIds = Array.isArray(parsed)
            ? [...new Set(parsed.map(String).filter((value) => /^[1-9]\d*$/.test(value)))].slice(0, 3)
            : [];
        } catch (_) { this.curatedIds = []; }
        if (!this.$root.open) {
          this.$root.dataset.state = 'closed';
          this.$root.showModal();
          this.track('faf_recommendation_opened', { curated_count: this.curatedIds.length });
          if (!window.FAFLayout?.isStickyDesktopPdp?.()) {
            window.FAFScrollLock?.lock?.('contextual-recommendations');
            this.locked = true;
          }
          this.$root.getBoundingClientRect();
          window.requestAnimationFrame(() => {
            if (!this.destroyed && this.$root.open && !this.closing) this.$root.dataset.state = 'open';
          });
        } else {
          this.$root.dataset.state = 'open';
        }
        if (this.$refs.scroll) this.$refs.scroll.scrollTop = 0;
        this.$refs.close?.focus({ preventScroll: true });
        this.load();
      },

      async load() {
        const token = ++this.sequence;
        this.controller?.abort();
        this.observer?.disconnect();
        this.observer = null;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.status = 'loading';
        this.count = 0;
        this.retryable = false;
        this.$refs.results.replaceChildren();
        this.$refs.results.classList.add('hidden');
        this.track('faf_recommendation_requested');
        try {
          const recommendations = this.$store.tagalysRecs;
          if (typeof recommendations?.getRecsForProduct !== 'function') throw new Error('adapter_unavailable');
          const config = {
            productId: this.sourceId,
            recId: this.$root.dataset.widgetId,
            limit: 6,
            view: 'contextual-recommendations',
            remoteMarkupId: 'FafContextualRecommendationCards',
            targetMarkupId: this.$refs.results.id,
            context: this.surface,
            list: 'contextual_recommendations_complete_look',
            orderedCards: true,
            excludeSource: true,
            trackImpressions: false,
            signal
          };
          let result = await recommendations.getRecsForProduct({
            ...config,
            ...(this.curatedIds.length ? { candidateIds: this.curatedIds } : {})
          });
          if (token !== this.sequence || !this.$root.open || this.destroyed) return;
          if (result.status === 'empty' && this.curatedIds.length) result = await recommendations.getRecsForProduct(config);
          if (token !== this.sequence || !this.$root.open || this.destroyed) return;
          if (!result || !['success', 'empty', 'error'].includes(result.status)) throw new Error('invalid_adapter_result');
          this.status = result.status;
          this.count = result.count || 0;
          this.retryable = result.retryable === true;
          this.track('faf_recommendation_results_loaded', {
            result_status: result.status, result_count: this.count, reason: result.reason || ''
          });
          if (result.status === 'success') {
            await this.$nextTick();
            if (token === this.sequence && this.$root.open) this.observeCards();
          }
        } catch (error) {
          if (token !== this.sequence || !this.$root.open || this.destroyed) return;
          this.status = 'error';
          this.retryable = true;
          this.track('faf_recommendation_results_loaded', {
            result_status: 'error', result_count: 0, reason: 'ui_failed'
          });
          console.warn('On-demand recommendations failed.', error);
        }
      },

      observeCards() {
        if (typeof window.IntersectionObserver !== 'function') return;
        const cards = Array.from(this.$refs.results.querySelectorAll('[data-elevar-product-card]'));
        const seen = new Set();
        const token = this.sequence;
        const observer = new IntersectionObserver((entries) => {
          if (token !== this.sequence || !this.$root.open || this.status !== 'success') return;
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || seen.has(entry.target)) return;
            seen.add(entry.target);
            observer.unobserve(entry.target);
            try {
              Promise.resolve(window.FAFAnalytics?.viewItemListCards?.([entry.target], {
                list: 'contextual_recommendations_complete_look',
                startPosition: cards.indexOf(entry.target)
              })).catch((error) => console.warn('Recommendation impression failed.', error));
            } catch (error) { console.warn('Recommendation impression failed.', error); }
          });
        }, { root: this.$refs.scroll, threshold: 0.5 });
        this.observer = observer;
        cards.forEach((card) => observer.observe(card));
      },

      retry() {
        if (this.status === 'loading' || !this.$root.open || !this.retryable) return;
        this.load();
      },

      close() {
        if (!this.$root.open || this.closing) return;
        this.clearCloseWait();
        this.closing = true;
        this.$root.dataset.state = 'closed';
        const finish = () => {
          if (!this.closing) return;
          this.clearCloseWait();
          this.closing = false;
          if (this.$root.open) this.$root.close();
        };
        this.closeTransitionHandler = (event) => {
          if (event.target !== this.$root || event.propertyName !== 'transform') return;
          finish();
        };
        this.$root.addEventListener('transitionend', this.closeTransitionHandler);
        this.closeTimer = window.setTimeout(finish, 420);
      },

      afterClose() {
        if (this.$root.open) return;
        this.clearCloseWait();
        this.closing = false;
        this.$root.dataset.state = 'closed';
        if (this.status === 'idle' && !this.controller && !this.trigger && !this.locked && !this.observer) return;
        const token = ++this.sequence;
        this.controller?.abort();
        this.controller = null;
        this.observer?.disconnect();
        this.observer = null;
        if (this.locked) window.FAFScrollLock?.unlock?.('contextual-recommendations');
        this.locked = false;
        this.status = 'idle';
        this.$refs.results?.replaceChildren();
        const trigger = this.trigger;
        this.trigger = null;
        this.$nextTick(() => {
          if (!this.destroyed && token === this.sequence && !this.$root.open && trigger?.isConnected) trigger.focus({ preventScroll: true });
        });
      },

      destroy() {
        if (this.cartAddedHandler) {
          window.removeEventListener('cart-added', this.cartAddedHandler);
          this.cartAddedHandler = null;
        }
        this.destroyed = true;
        this.close();
      }
    }));
  };
  if (window.Alpine) register();
  document.addEventListener('alpine:init', register, { once: true });
})();
